import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getProjects, closeProject, deleteProject, getSeedContentHidden } from '../../services/firestoreService';
import { SEED_PROJECTS } from '../../data/seedProjects';
import { useAuth } from '../../contexts/AuthContext';
import { formatDate, toJsDate } from '../../utils/formatDate';
import ApplicationModal from '../../components/applications/ApplicationModal';

const ProjectBoard = () => {
  const { currentUser } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [seedHidden, setSeedHidden] = useState(false); // admin can hide demo content

  useEffect(() => {
    console.log('ProjectBoard component mounted, loading projects...');
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Loading projects from Firebase...');
      const projectsData = await getProjects(20); // Load up to 20 projects
      console.log('Projects loaded successfully:', projectsData);
      console.log('Number of projects:', projectsData.length);

      setProjects(projectsData);
      const hidden = await getSeedContentHidden();
      setSeedHidden(hidden);
    } catch (error) {
      console.error('Error loading projects:', error);
      setError('Failed to load projects. Please try again.');
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };




  // State for filtering and search
  const [searchTerm, setSearchTerm] = useState('');
  const [industryFilter, setIndustryFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [specialtyFilter, setSpecialtyFilter] = useState('');
  const [showFeaturedOnly, setShowFeaturedOnly] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'grid'

  // State for application modal
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [selectedProjectForApplication, setSelectedProjectForApplication] = useState(null);

  // State for pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [projectsPerPage] = useState(10);

  // Simple function to check if project should show closed message
  const shouldShowClosedMessage = (project) => {
    return project.status === 'closed';
  };

  // Function to handle marking project as filled
  const handleMarkAsFilled = async (projectId) => {
    if (!currentUser) return;

    try {
      await closeProject(projectId, currentUser.uid);
      // Refresh the projects list
      loadProjects();
      alert('Project marked as filled successfully!');
    } catch (error) {
      console.error('Error marking project as filled:', error);
      alert('Failed to mark project as filled. Please try again.');
    }
  };

  // Function to check if current user owns the project
  const isProjectOwner = (project) => {
    return currentUser && (project.postedBy === currentUser.uid || project.contactEmail === currentUser.email);
  };

  // Function to check if current user is admin
  const isAdmin = () => {
    return currentUser && currentUser.email === 'admin@the-apn.com';
  };

  // Function to handle deleting project (admin only)
  const handleDeleteProject = async (projectId, projectTitle) => {
    if (!isAdmin()) return;

    const confirmDelete = window.confirm(
      `Are you sure you want to permanently delete the project "${projectTitle}"? This action cannot be undone.`
    );

    if (!confirmDelete) return;

    try {
      await deleteProject(projectId);
      // Refresh the projects list
      loadProjects();
      alert('Project deleted successfully!');
    } catch (error) {
      console.error('Error deleting project:', error);
      alert('Failed to delete project. Please try again.');
    }
  };

  // Get unique values for filter dropdowns
  const locations = [...new Set(projects.map(project => project.location))];

  // Predefined industries with dynamic ones from projects
  const predefinedIndustries = [
    'Technology',
    'Financial Services',
    'Healthcare',
    'Real Estate',
    'Manufacturing',
    'Pharmaceuticals',
    'Venture Capital',
    'Industrial'
  ];
  const projectIndustries = [...new Set(projects.map(project => project.industry))];
  const industries = [...new Set([...predefinedIndustries, ...projectIndustries])].filter(Boolean);

  // Predefined specialties with dynamic ones from projects
  const predefinedSpecialties = [
    'Management Consultant/Advisory',
    'Valuation',
    'Accounting',
    'Legal',
    'Underwriter/Investor',
    'Bank(IB)'
  ];
  // Use real projects data only
  // Real Firestore projects + demo/seed projects (hidden when admin toggles demo off).
  const displayProjects = seedHidden ? projects : [...projects, ...SEED_PROJECTS];

  const projectSpecialties = [...new Set(displayProjects.map(project => project.specialty))];
  const specialties = [...new Set([...predefinedSpecialties, ...projectSpecialties])].filter(Boolean);

  // Filter projects based on search and filters
  const filteredProjects = displayProjects.filter(project => {
    const matchesSearch =
      (project.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (project.description || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (project.client || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (project.skills || []).some(skill => skill.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesIndustry = industryFilter === '' || project.industry === industryFilter;
    const matchesLocation = locationFilter === '' || project.location === locationFilter;
    const matchesSpecialty = specialtyFilter === '' || project.specialty === specialtyFilter;
    const matchesFeatured = !showFeaturedOnly || project.featured;

    return matchesSearch && matchesIndustry && matchesLocation && matchesSpecialty && matchesFeatured;
  });

  // Sort projects by posted date (newest first)
  const sortedProjects = [...filteredProjects].sort((a, b) =>
    (toJsDate(b.postedDate)?.getTime() || 0) - (toJsDate(a.postedDate)?.getTime() || 0)
  );

  // Pagination logic
  const totalPages = Math.ceil(sortedProjects.length / projectsPerPage);
  const startIndex = (currentPage - 1) * projectsPerPage;
  const endIndex = startIndex + projectsPerPage;
  const currentProjects = sortedProjects.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      handlePageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      handlePageChange(currentPage + 1);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-apn-blue mx-auto mb-4"></div>
          <p>Loading projects from Firebase...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={loadProjects}
            className="bg-apn-blue text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white">
      {/* Page Header */}
      <div className="bg-apn-blue text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            {/* APN Logo */}
            <div className="flex justify-center mb-4">
              <img
                src={`${process.env.PUBLIC_URL}/apn-logos/APN-Tag-white.jpg`}
                alt="APN Logo"
                className="h-16"
              />
            </div>
            <h1 className="text-3xl font-bold">Project Board</h1>
          </div>
        </div>
      </div>

      {/* Project Board Content */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Search and Filters */}
          <div className="mb-8 space-y-4">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <div className="w-full md:w-1/2">
                <input
                  type="text"
                  placeholder="Search projects by title, client, or skills..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                />
              </div>
              <div className="flex space-x-4">
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-md ${
                    viewMode === 'list'
                      ? 'bg-apn-blue text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-md ${
                    viewMode === 'grid'
                      ? 'bg-apn-blue text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label htmlFor="industry-filter" className="block text-sm font-medium text-gray-700 mb-1">
                  Filter by Industry
                </label>
                <select
                  id="industry-filter"
                  value={industryFilter}
                  onChange={(e) => setIndustryFilter(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                >
                  <option value="">All Industries</option>
                  <option value="Technology">Technology</option>
                  <option value="Financial Services">Financial Services</option>
                  <option value="Healthcare">Healthcare</option>
                  <option value="Real Estate">Real Estate</option>
                  <option value="Manufacturing">Manufacturing</option>
                  <option value="Pharmaceuticals">Pharmaceuticals</option>
                  <option value="Venture Capital">Venture Capital</option>
                  <option value="Industrial">Industrial</option>
                  {industries.filter(industry => !['Technology', 'Financial Services', 'Healthcare', 'Real Estate', 'Manufacturing', 'Pharmaceuticals', 'Venture Capital', 'Industrial'].includes(industry)).map((industry, index) => (
                    <option key={`extra-${index}`} value={industry}>{industry}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="location-filter" className="block text-sm font-medium text-gray-700 mb-1">
                  Filter by Location
                </label>
                <select
                  id="location-filter"
                  value={locationFilter}
                  onChange={(e) => setLocationFilter(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                >
                  <option value="">All Locations</option>
                  {locations.map((location, index) => (
                    <option key={index} value={location}>{location}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="specialty-filter" className="block text-sm font-medium text-gray-700 mb-1">
                  Filter by Specialty
                </label>
                <select
                  id="specialty-filter"
                  value={specialtyFilter}
                  onChange={(e) => setSpecialtyFilter(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                >
                  <option value="">All Specialties</option>
                  <option value="Management Consultant/Advisory">Management Consultant/Advisory</option>
                  <option value="Valuation">Valuation</option>
                  <option value="Accounting">Accounting</option>
                  <option value="Legal">Legal</option>
                  <option value="Underwriter/Investor">Underwriter/Investor</option>
                  <option value="Bank(IB)">Bank(IB)</option>
                  {specialties.filter(specialty => !['Management Consultant/Advisory', 'Valuation', 'Accounting', 'Legal', 'Underwriter/Investor', 'Bank(IB)'].includes(specialty)).map((specialty, index) => (
                    <option key={`extra-${index}`} value={specialty}>{specialty}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-end">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={showFeaturedOnly}
                    onChange={() => setShowFeaturedOnly(!showFeaturedOnly)}
                    className="h-4 w-4 text-apn-blue focus:ring-apn-blue border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Show Featured Projects Only</span>
                </label>
              </div>
            </div>
          </div>

          {/* Results Count */}
          <div className="mb-6 flex justify-between items-center">
            <p className="text-gray-600">
              Showing {startIndex + 1}-{Math.min(endIndex, sortedProjects.length)} of {sortedProjects.length} projects
            </p>
            <Link
              to="/projects/post"
              className="inline-flex items-center bg-apn-blue text-white py-2 px-4 rounded hover:bg-blue-700 transition duration-200"
            >
              <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Post a Project
            </Link>
          </div>

          {/* Project Listings */}
          {currentProjects.length > 0 ? (
            viewMode === 'list' ? (
              <div className="space-y-6">
                {currentProjects.map(project => (
                  <div
                    key={project.id}
                    className={`bg-white rounded-lg shadow-lg overflow-hidden border ${
                      project.featured ? 'border-apn-blue' : 'border-gray-200'
                    }`}
                  >
                    <div className="p-6">
                      <div className="flex flex-col md:flex-row justify-between mb-4">
                        <div>
                          <h3 className="text-xl font-bold">{project.title}</h3>
                          <p className="text-gray-600">{project.client} • {project.industry} • {project.location}</p>
                          <p className="text-gray-600 mt-1">Specialty: <span className="font-medium">{project.specialty}</span></p>
                        </div>
                        <div className="mt-2 md:mt-0 flex items-start">
                          {project.featured && (
                            <span className="inline-block bg-apn-blue text-white text-xs px-2 py-1 rounded mr-2">
                              Featured
                            </span>
                          )}
                          <span className="inline-block bg-green-100 text-green-800 text-xs px-2 py-1 rounded">
                            {project.status}
                          </span>
                        </div>
                      </div>
                      <p className="text-gray-600 mb-4">{project.description}</p>
                      <div className="mb-4">
                        <h4 className="font-semibold text-gray-700 mb-2">Requirements:</h4>
                        <ul className="list-disc pl-5 text-gray-600 space-y-1">
                          {(Array.isArray(project.requirements) ? project.requirements : []).map((req, index) => (
                            <li key={index}>{req}</li>
                          ))}
                        </ul>
                      </div>
                      <div className="mb-4">
                        <h4 className="font-semibold text-gray-700 mb-2">Skills:</h4>
                        <div className="flex flex-wrap gap-2">
                          {(Array.isArray(project.skills) ? project.skills : []).map((skill, index) => (
                            <span key={index} className="inline-block bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded">
                              {skill}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
                        <div className="mb-4 md:mb-0 space-y-1">
                          <div>
                            <span className="text-gray-700 font-semibold">Budget:</span> <span className="text-gray-600">{project.budget}</span>
                          </div>
                          <div>
                            <span className="text-gray-700 font-semibold">Duration:</span> <span className="text-gray-600">{project.duration}</span>
                          </div>
                          <div>
                            <span className="text-gray-700 font-semibold">Deadline:</span> <span className="text-gray-600">{formatDate(project.deadline)}</span>
                          </div>
                          <p className="text-gray-500 text-sm">Posted on {formatDate(project.postedDate)}</p>
                        </div>
                        <div className="flex flex-wrap gap-3">
                          {shouldShowClosedMessage(project) ? (
                            <div className="inline-block bg-red-100 text-red-800 py-2 px-6 rounded border border-red-300">
                              No longer accepting applications
                            </div>
                          ) : (
                            <>
                              <button
                                onClick={() => {
                                  setSelectedProjectForApplication(project);
                                  setShowApplicationModal(true);
                                }}
                                className="inline-block bg-apn-blue text-white py-2 px-6 rounded hover:bg-blue-700 transition duration-200"
                              >
                                Apply Now
                              </button>
                              <Link
                                to={`/projects/${project.id}`}
                                className="inline-block bg-white text-apn-blue border border-apn-blue py-2 px-6 rounded hover:bg-gray-50 transition duration-200"
                              >
                                Refer Someone
                              </Link>
                            </>
                          )}
                          {isProjectOwner(project) && project.status !== 'closed' && (
                            <button
                              onClick={() => handleMarkAsFilled(project.id)}
                              className="inline-block bg-orange-600 text-white py-2 px-6 rounded hover:bg-orange-700 transition duration-200"
                            >
                              Mark as Filled
                            </button>
                          )}
                          {isAdmin() && (
                            <button
                              onClick={() => handleDeleteProject(project.id, project.title)}
                              className="inline-block bg-red-600 text-white py-2 px-6 rounded hover:bg-red-700 transition duration-200"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {currentProjects.map(project => (
                  <div
                    key={project.id}
                    className={`bg-white rounded-lg shadow-lg overflow-hidden border h-full flex flex-col ${
                      project.featured ? 'border-apn-blue' : 'border-gray-200'
                    }`}
                  >
                    <div className="p-6 flex-grow">
                      <div className="flex justify-between items-start mb-4">
                        <h3 className="text-lg font-bold">{project.title}</h3>
                        {project.featured && (
                          <span className="inline-block bg-apn-blue text-white text-xs px-2 py-1 rounded">
                            Featured
                          </span>
                        )}
                      </div>
                      <p className="text-gray-600 mb-2">{project.client}</p>
                      <p className="text-gray-600 mb-2">Specialty: <span className="font-medium">{project.specialty}</span></p>
                      <div className="space-y-2 mb-4">
                        <div className="flex items-center text-gray-600">
                          <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                          <span>{project.industry}</span>
                        </div>
                        <div className="flex items-center text-gray-600">
                          <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <span>{project.location}</span>
                        </div>
                        <div className="flex items-center text-gray-600">
                          <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span>{project.duration}</span>
                        </div>
                        <div className="flex items-center text-gray-600">
                          <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span>{project.budget}</span>
                        </div>
                      </div>
                      <p className="text-gray-600 mb-4 line-clamp-3">{project.description}</p>
                      <div className="flex flex-wrap gap-2 mb-4">
                        {(Array.isArray(project.skills) ? project.skills : []).slice(0, 3).map((skill, index) => (
                          <span key={index} className="inline-block bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded">
                            {skill}
                          </span>
                        ))}
                        {(project.skills || []).length > 3 && (
                          <span className="inline-block bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded">
                            +{(project.skills || []).length - 3} more
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="p-6 bg-gray-50 border-t border-gray-200">
                      <div className="flex justify-between items-center">
                        <div>
                          <span className="inline-block bg-green-100 text-green-800 text-xs px-2 py-1 rounded mr-2">
                            {project.status}
                          </span>
                          <span className="text-gray-500 text-xs">Deadline: {formatDate(project.deadline)}</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <Link
                            to={`/projects/${project.id}`}
                            className="inline-block bg-apn-blue text-white py-1 px-3 rounded text-sm hover:bg-blue-700 transition duration-200"
                          >
                            View Details
                          </Link>
                          {shouldShowClosedMessage(project) ? (
                            <div className="inline-block bg-red-100 text-red-800 py-1 px-3 rounded text-sm border border-red-300">
                              No longer accepting applications
                            </div>
                          ) : (
                            <>
                              <button
                                onClick={() => {
                                  setSelectedProjectForApplication(project);
                                  setShowApplicationModal(true);
                                }}
                                className="inline-block bg-green-600 text-white py-1 px-3 rounded text-sm hover:bg-green-700 transition duration-200"
                              >
                                Apply Now
                              </button>
                              <Link
                                to={`/projects/${project.id}`}
                                className="inline-block bg-white text-apn-blue border border-apn-blue py-1 px-3 rounded text-sm hover:bg-gray-50 transition duration-200"
                              >
                                Refer
                              </Link>
                            </>
                          )}
                          {isProjectOwner(project) && project.status !== 'closed' && (
                            <button
                              onClick={() => handleMarkAsFilled(project.id)}
                              className="inline-block bg-orange-600 text-white py-1 px-3 rounded text-sm hover:bg-orange-700 transition duration-200"
                            >
                              Filled
                            </button>
                          )}
                          {isAdmin() && (
                            <button
                              onClick={() => handleDeleteProject(project.id, project.title)}
                              className="inline-block bg-red-600 text-white py-1 px-3 rounded text-sm hover:bg-red-700 transition duration-200"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : (
            <div className="text-center py-12">
              <p className="text-xl text-gray-600">No projects found matching your search criteria.</p>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-12 flex justify-center">
              <nav className="inline-flex rounded-md shadow">
                <button
                  onClick={handlePrevious}
                  disabled={currentPage === 1}
                  className={`px-4 py-2 rounded-l-md border border-gray-300 ${
                    currentPage === 1
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Previous
                </button>

                {/* Page Numbers */}
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`px-4 py-2 border-t border-b border-gray-300 ${
                      currentPage === page
                        ? 'bg-apn-blue text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {page}
                  </button>
                ))}

                <button
                  onClick={handleNext}
                  disabled={currentPage === totalPages}
                  className={`px-4 py-2 rounded-r-md border border-gray-300 ${
                    currentPage === totalPages
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Next
                </button>
              </nav>
            </div>
          )}
        </div>
      </section>





      {/* Call to Action */}
      <section className="bg-apn-blue text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* APN Logo */}
          <div className="flex justify-center mb-6">
            <img
              src={`${process.env.PUBLIC_URL}/apn-logos/APN-Tag-SQ-white.jpg`}
              alt="APN Logo Square"
              className="h-20"
            />
          </div>
          <h2 className="text-3xl font-bold mb-4">Need Help with a Project?</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Post your project on the APN Project Board to connect with qualified finance professionals.
          </p>
          <Link to="/projects/post" className="btn-primary bg-white text-apn-blue hover:bg-gray-100">
            Post a Project
          </Link>
        </div>
      </section>

      {/* Application Modal */}
      {showApplicationModal && selectedProjectForApplication && (
        <ApplicationModal
          isOpen={showApplicationModal}
          onClose={() => {
            setShowApplicationModal(false);
            setSelectedProjectForApplication(null);
          }}
          posting={selectedProjectForApplication}
          type="project"
        />
      )}
    </div>
  );
};

export default ProjectBoard;
